import React, { useState } from 'react';
import { Trash2, Mail } from 'lucide-react';
import Logo from './assets/Logo.png';

const ConfirmDialog = ({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-slate-800 rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-white/10">
        <div className="p-6">
          <h3 className="text-xl font-semibold text-white mb-2">{title}</h3>
          <p className="text-sm text-white/70 mb-6">{message}</p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            >
              {cancelText}
            </button>
            <button
              onClick={onConfirm}
              className="px-4 py-2 rounded-xl text-sm font-medium bg-cyan-500 text-white hover:bg-cyan-400 transition-colors shadow-lg"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const DeleteUserDataKarigarHQ = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const isEmailValid = (value) => {
    const trimmed = value.trim();
    if (!trimmed) return false;
    // Simple but robust-enough email validation
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (!isEmailValid(email)) return;

    // Simulate API call with loading state
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccessDialog(true);
    }, 1500);
  };

  const emailHasValue = Boolean(email.trim());
  const canSubmit = !isSubmitting && emailHasValue && isEmailValid(email);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center px-4 py-10 text-slate-900">
      {/* Top branding */}
      <header className="w-full max-w-5xl flex items-center justify-center mb-10">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-xl px-4 py-3 shadow-sm border border-slate-200 flex items-center gap-2">
            <img
              src={Logo}
              alt="KarigarHQ Logo"
              className="h-8 w-auto"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="w-px h-6 bg-slate-200 mx-2"></div>
            <span className="text-xs font-semibold text-slate-500 tracking-[0.14em] uppercase">
              Privacy Center
            </span>
          </div>
        </div>
      </header>

      {/* Main card */}
      <div className="w-full max-w-2xl bg-white rounded-3xl shadow-xl border border-slate-100 px-8 py-10 flex flex-col items-center text-center">
        {/* Icon circle */}
        <div className="w-16 h-16 rounded-2xl bg-red-50 flex items-center justify-center mb-6 border border-red-100">
          <Trash2 className="w-8 h-8 text-red-500" />
        </div>

        {/* Heading */}
        <h1 className="text-3xl md:text-4xl font-bold mb-3 tracking-tight text-slate-800">
          Delete KarigarHQ Data
        </h1>
        <p className="text-sm md:text-base text-slate-500 max-w-xl mb-8">
          Request deletion of your KarigarHQ account and personal data. We take data
          privacy seriously and will process your request in accordance with applicable
          privacy laws.
        </p>

        {/* Inner panel */}
        <div className="w-full bg-slate-50 rounded-2xl border border-slate-200 px-6 py-6 md:px-8 md:py-8 text-left">
          <p className="text-sm text-slate-600 mb-6">
            To request deletion of your KarigarHQ account and associated data, please enter your
            registered email address below. You will receive a confirmation email with
            next steps.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email field */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <label className="block text-slate-700 text-sm font-medium">
                  Email Address
                </label>
              </div>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400">
                  <Mail className="w-4 h-4" />
                </span>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Enter your registered email"
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-white border border-slate-300 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all"
                />
              </div>
              {!email.trim() ? (
                <p className="mt-1 text-xs text-slate-500">
                  Use the email associated with your KarigarHQ account.
                </p>
              ) : !isEmailValid(email) ? (
                <p className="mt-1 text-xs text-red-500">
                  Please enter a valid email address.
                </p>
              ) : null}
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full mt-4 flex items-center justify-center gap-2 py-3 px-4 rounded-xl text-sm font-semibold text-white bg-red-500 hover:bg-red-600 focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all"
            >
              <Trash2 className="w-4 h-4" />
              <span>{isSubmitting ? 'Submitting request...' : 'Request Data Deletion'}</span>
            </button>
          </form>
        </div>
      </div>

      {/* What happens next */}
      <div className="w-full max-w-2xl mt-10">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-5 md:px-8 md:py-6">
          <h2 className="text-base md:text-lg font-semibold mb-3 text-slate-800">
            What happens next?
          </h2>
          <p className="text-xs md:text-sm text-slate-600 mb-4">
            Once you submit your request, the following steps will take place:
          </p>
          <ul className="space-y-2 text-xs md:text-sm text-slate-600 list-disc list-inside">
            <li>We will verify your account details.</li>
            <li>Your data deletion request will be processed within 24 hours.</li>
            <li>You will receive a confirmation email once the deletion is completed.</li>
            <li>All your personal data associated with this account will be permanently removed.</li>
            <li className="text-red-600 font-semibold">This action is irreversible and cannot be undone.</li>
          </ul>
        </div>
      </div>

      {/* Completion dialog */}
      <ConfirmDialog
        isOpen={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        onConfirm={() => setShowSuccessDialog(false)}
        title="Deletion request submitted"
        message={`We have received your deletion request for ${email.trim()}. If this email is associated with a KarigarHQ account, you will receive a confirmation email with further instructions shortly.`}
        confirmText="Got it"
        cancelText="Close"
      />
    </div>
  );
};

export default DeleteUserDataKarigarHQ;
